import { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { CellContext, createColumnHelper } from '@tanstack/react-table'

import Stack from '@mui/material/Stack'
import Tooltip from '@mui/material/Tooltip'
import LaunchIcon from '@mui/icons-material/Launch'
import StarBorderRoundedIcon from '@mui/icons-material/StarBorderRounded'
import StarRoundedIcon from '@mui/icons-material/StarRounded'

import ChipButton from '@/components/ui/buttons/ChipButton'
import Table from '@/components/ui/tables/Table'

export type WaitingRoomData = {
  id: string
  isPrioritized?: boolean
  name: string
}

type WaitingRoomsTableProps = {
  data: WaitingRoomData[]
  loading?: boolean
  onPageChange?: (newPage: number, rowsPerPage: number) => void
  rowsPerPageOptions?: number[]
  totalCount: number
}

const columnHelper = createColumnHelper<WaitingRoomData>()

export function WaitingRoomsTable(props: WaitingRoomsTableProps) {
  const { data, loading = false } = props

  const { t } = useTranslation()

  const columns = useMemo(() => {
    return [
      columnHelper.accessor('name', {
        header: t('page.waitingRooms.waitingRoomsTable.header.name').toString(),
      }),
      columnHelper.accessor('isPrioritized', {
        header: t('page.waitingRooms.waitingRoomsTable.header.priority').toString(),
        cell: ({ getValue }) => <Stack alignItems="center">{getValue() ? <StarRoundedIcon color="primary" /> : <StarBorderRoundedIcon />}</Stack>,
        meta: { align: 'center' },
      }),
      columnHelper.display({
        id: 'actions',
        header: t('common:table.header.action'),
        cell: ActionsCell,
        meta: { align: 'right' },
      }),
    ]
  }, [t])

  return <Table columns={columns} data={data} loading={loading} />
}

function ActionsCell({ row }: CellContext<WaitingRoomData, unknown>) {
  const navigate = useNavigate()
  const { t } = useTranslation()
  return (
    <Stack justifyContent="flex-end" direction="row" spacing={1}>
      <Tooltip title={t('page.waitingRooms.waitingRoomsTable.openWaitingRoomButton.tooltip')}>
        <ChipButton
          color="info"
          onClick={() => {
            navigate(`/super-admin/waiting-rooms/${row.original.id}`)
          }}
        >
          <LaunchIcon />
        </ChipButton>
      </Tooltip>
    </Stack>
  )
}

export default WaitingRoomsTable
